import type {ReactElement} from 'react';
import React from 'react';
import {Text, TextSize} from '@Components/text';
import {noop} from '@Utils/general.util';
import {ClickableDiv} from '@Components/clickable-div';
import type {ColorPaletteInfo} from '@Components/auto-branding/auto-branding.types';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {getUniqueString} from '@Utils/string.util';
import styles from './color-palette.module.scss';

export enum ColorPaletteSize {
  SMALL = 'small',
  MEDIUM = 'medium',
}

export interface ColorPaletteProps {
  appliedPalette?: ColorPaletteInfo | null;
  size?: ColorPaletteSize;
  selected?: boolean;
  beingApplied?: boolean;
  showNameOverlayOnHover?: boolean;
  showNameLabel?: boolean;
  onClick?: VoidFunction;
}

const DEFAULT_COLORS = ['#D4D4D4', '#ABABAB', '#C3C3C3', '#D5D5D5', '#E4E4E4'];

export function ColorPalette({
  appliedPalette = null,
  size = ColorPaletteSize.MEDIUM,
  selected = false,
  beingApplied = false,
  showNameOverlayOnHover = false,
  showNameLabel = false,
  onClick = noop,
}: ColorPaletteProps): ReactElement {
  const getSizeModifier = (): string => {
    switch (size) {
      case ColorPaletteSize.SMALL:
        return styles.small;
      case ColorPaletteSize.MEDIUM:
      default:
        return '';
    }
  };

  const getNameComponent = (): ReactElement | null => {
    if (!showNameLabel || !appliedPalette?.name) {
      return null;
    }

    return <Text val={appliedPalette.name} size={TextSize.XSMALL} className={`${selected ? 'content-primary' : 'content-sub-text'}`} bold={selected} />;
  };

  const getOverlayComponent = (): ReactElement | null => {
    if (beingApplied) {
      return (
        <div className={`${styles.overlay} ${styles.overlayVisible}`}>
          <div className={`${styles.loadingContainer}`}>
            <Icon icon="icon-loader" className={`${styles.loadingIcon} content-body-white`} size={IconSize.SIZE_ICON_20} />
            <Text val={window.i18next.t('pmwjs_applying_ellipsis')} size={TextSize.SMALL} className="content-body-white body-s" />
          </div>
        </div>
      );
    }

    if (!showNameOverlayOnHover || !appliedPalette?.name) {
      return null;
    }

    return (
      <div className={`${styles.overlay}`}>
        <Text val={appliedPalette.name} size={TextSize.XSMALL} bold className={`content-body-white ${styles.overlayText}`} />
      </div>
    );
  };

  const getColorsToDisplay = (): string[] => {
    if (!appliedPalette || appliedPalette?.colors.length === 0) {
      return DEFAULT_COLORS;
    }

    return appliedPalette.colors;
  };

  const getClassNamesForPalette = (): string => {
    const isSelectable = onClick && onClick !== noop;

    return `${styles.colorPalette} ${isSelectable ? styles.selectable : ''} ${selected ? styles.selected : ''}`;
  };

  const clickHandler = (): void => {
    if (!selected) {
      onClick();
    }
  };

  const colorsToShow = getColorsToDisplay();
  const sizeModifier = getSizeModifier();

  return (
    <div className={`${styles.colorPaletteContainer}`}>
      <ClickableDiv className={`${getClassNamesForPalette()} ${sizeModifier}`} onClick={clickHandler}>
        {colorsToShow.map((color) => {
          return <div key={getUniqueString()} className={`${styles.colorBlock} ${sizeModifier}`} style={{backgroundColor: color}} />;
        })}
        {getOverlayComponent()}
      </ClickableDiv>
      {getNameComponent()}
    </div>
  );
}
